import { VideoEditor } from '../VideoEditor';
import { EditorChangeType } from '../types';
import { Timeline } from '../models/Timeline';
import { Track } from '../models/Track';
import { Element } from '../models/Element';
import { VideoElement } from '../models/elements/VideoElement';
import { TimelineMode } from '../commands/TimelineMode';
import { AudioElement } from '../models';
import { pickUnderlyingVideoElement } from '@src/utility/stackedLayout';
import { ExtraElementData } from '../models/ExtraElementData';
import { LayoutElementsResult } from '@src/utility/stackedLayout';
import { Video } from '@src/types.ts/story';

/**
 * TrackManager is a utility class responsible for managing tracks and elements
 * within a Timeline in the video editing context.
 *
 * It provides functionality to create and remove tracks, add or remove elements,
 * merge multiple elements into one, and split an element into multiple parts.
 * The class integrates with the VideoEditor's batching mechanism to optimize
 * performance and maintain state consistency.
 */
export class TrackManager {
  /**
   * Instance variables for Timeline and VideoEditor
   */
  private editor: VideoEditor;

  private _extraElementData: ExtraElementData | null = null;

  /**
   * Creates a new TrackManager instance
   * @param editor Optional VideoEditor reference for dirty marking and notifications
   */
  constructor(editor: VideoEditor) {
    this.editor = editor;
  }

  /**
   * Small tolerance value for floating-point comparisons
   * @private
   */
  private epsilon: number = 0.001;

  /**
   * Marks the timeline as dirty if an editor is available
   * @param source Description of what caused the change
   */
  private markDirty(source: string): void {
    if (this.editor) {
      this.editor.markDirty(`trackManager: ${source}`);
    }
  }

  /**
   * Notifies observers of a change if an editor is available
   * @param changeType Type of change that occurred
   * @param payload Additional data about the change
   */
  private notifyObservers(changeType: EditorChangeType, payload?: any): void {
    if (this.editor) {
      this.editor.notifyObservers(changeType, payload);
    }
  }

  //
  // External interfaces - public methods for timeline operations
  //
  /**
   * Moves an element to a new position on the timeline
   * @param element Element to move
   * @param newStartTime New start time for the element
   * @param targetTrack Optional target track if moving between tracks
   * @param mode Optional TimelineMode to use for this operation
   * @returns true if successful, false otherwise
   */
  public shiftElement(
    element: Element,
    newStartTime: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
    snapToFrame: boolean = true,
  ): boolean {
    if (!element) return false;

    // Get source track
    const sourceTrack = this.editor.timeline.getTrackForElement(element.id);
    if (!sourceTrack) return false;

    // Snap time to frame and ensure it's non-negative
    if (snapToFrame) {
      newStartTime = this.editor.timeline.snapTimeToFrame(newStartTime);
    }
    newStartTime = Math.max(0, newStartTime);

    // Determine target track
    const destinationTrack = sourceTrack;

    // Store original values for change notification
    const originalTime = element.time;
    const originalTrackId = sourceTrack.id;

    // Handle track change if needed
    if (destinationTrack.id !== sourceTrack.id) {
      if (!this.moveElementToTrack(element, destinationTrack)) {
        return false;
      }
    }

    // Determine final position based on timeline mode
    let finalStartTime =
      mode === TimelineMode.OVERLAP || mode === TimelineMode.RIPPLE
        ? newStartTime
        : this.calculateValidStartTime(
            destinationTrack,
            element,
            newStartTime,
            element.time,
            element.durationSeconds,
          );

    // Check if movement is necessary
    const shiftDistance = finalStartTime - element.time;
    if (shiftDistance === 0 && destinationTrack.id === sourceTrack.id) {
      return true;
    }

    // Apply the shift based on mode
    switch (mode) {
      case TimelineMode.DEFAULT:
        // In DEFAULT mode, we've already calculated a valid finalStartTime that avoids collisions
        element.update({ time: finalStartTime });
        break;

      case TimelineMode.RIPPLE:
        // Shift the element to the snapped position
        element.update({ time: finalStartTime });
        // Shift elements after the new position
        const affectedElements = this.getElementsAfterTime(
          destinationTrack,
          finalStartTime,
          element,
        );
        // Only shift if there are elements to move
        if (affectedElements.length > 0) {
          this.applyTimeDeltaToElements(affectedElements, shiftDistance);
        }
        break;

      case TimelineMode.OVERLAP:
        element.update({ time: finalStartTime });
        break;
    }

    // Update timeline duration
    this.editor.timeline.recalculateDuration();

    // Mark as dirty
    this.markDirty(`moveElement (${element.id})`);

    // Notify observers about the element move
    this.notifyObservers(EditorChangeType.ELEMENT_SHIFTED, {
      element: element,
      previousTime: originalTime,
      newTime: finalStartTime,
      previousTrackId: originalTrackId,
      newTrackId: destinationTrack.id,
      moveDistance: shiftDistance,
      mode: mode,
    });

    return true;
  }

  /**
   * Trims the start of an element, adjusting start time and duration
   * Handles both general and video-specific trimming logic
   * @param element The element to trim
   * @param newStartTime New start time for the element
   * @param mode Optional TimelineMode to use for this operation
   * @returns true if successful, false otherwise
   */
  public trimElementStart(
    element: Element,
    newStartTime: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
    snapToFrame: boolean = true,
  ): boolean {
    if (!element) return false;

    // Ensure time is non-negative and frame-snapped
    if (snapToFrame) {
      newStartTime = this.editor.timeline.snapTimeToFrame(newStartTime);
    }
    newStartTime = Math.max(0, newStartTime);

    // Get track
    const track = this.getTrackById(
      this.editor.timeline.getTrackIdForElement(element.id) || '',
    );
    if (!track) return false;

    // Handle elements with no duration (e.g., markers)
    if (element.duration === null) {
      // Store original state for notifications
      const originalTime = element.time;

      element.update({ time: newStartTime });

      // Mark as dirty and notify
      this.markDirty(`trimElementStart (${element.id}) - marker element`);
      this.notifyObservers(EditorChangeType.ELEMENT_TRIMMED_START, {
        element: element,
        previousTime: originalTime,
        newTime: newStartTime,
        mode: mode,
      });

      return true;
    }

    // Extract original properties
    const originalTime = element.time;
    const originalDuration = element.durationSeconds;
    const isVideoOrAudio = element.type === 'video' || element.type === 'audio';
    const originalTrimStart = isVideoOrAudio ? element.trim_start ?? 0 : 0;

    // Calculate potential trim_start based on desired new start time
    const timeDiff = newStartTime - originalTime;
    const potentialTrimStart = isVideoOrAudio
      ? originalTrimStart + timeDiff
      : 0;

    // Determine snapped start time based on mode (initially use newStartTime)
    let snappedStartTime = newStartTime;
    let newDuration = originalDuration - timeDiff; // Default duration adjustment

    // Adjust properties for video/audio elements if trim_start would be negative
    let newTrimStart: number | undefined;
    if (isVideoOrAudio) {
      if (potentialTrimStart >= 0) {
        // No adjustment needed; use the potential trim_start
        newTrimStart = potentialTrimStart;
        newDuration = originalDuration - timeDiff;
        snappedStartTime = newStartTime;
      } else {
        // Clamp trim_start to 0 and adjust time and duration
        newTrimStart = 0;
        const trimAdjustment = Math.abs(potentialTrimStart);
        snappedStartTime = newStartTime + trimAdjustment; // e.g., 0 + abs(-2) = 2
        newDuration = originalDuration + trimAdjustment; // e.g., 10 + 2 = 12
      }
    } else {
      // Non-video/audio elements just adjust duration
      newDuration = originalDuration - timeDiff;
    }

    // In DEFAULT mode, snap the start time to avoid collisions
    if (mode === TimelineMode.DEFAULT) {
      snappedStartTime = this.calculateValidStartTime(
        track,
        element,
        snappedStartTime,
        originalTime,
        newDuration,
      );
      // Recalculate timeDiff after snapping
      const adjustedTimeDiff = snappedStartTime - originalTime;
      if (isVideoOrAudio) {
        const adjustedPotentialTrimStart = originalTrimStart + adjustedTimeDiff;
        if (adjustedPotentialTrimStart >= 0) {
          newTrimStart = adjustedPotentialTrimStart;
          newDuration = originalDuration - adjustedTimeDiff;
        } else {
          newTrimStart = 0;
          const trimAdjustment = Math.abs(adjustedPotentialTrimStart);
          snappedStartTime = newStartTime + trimAdjustment;
          newDuration = originalDuration + trimAdjustment;
        }
      } else {
        newDuration = originalDuration - adjustedTimeDiff;
      }
    }

    // Validate new duration
    if (newDuration <= 0) return false;

    // Prepare updates
    const updates: Record<string, any> = {
      time: snappedStartTime,
      duration: newDuration,
    };
    if (isVideoOrAudio) {
      updates.trim_start = newTrimStart;
    }

    element.update(updates);

    // Refresh transcript segment for video elements
    if (element.type === 'video') {
      (element as VideoElement).invalidateTranscriptSegment();
    }

    // Apply ripple effect in RIPPLE mode
    if (mode === TimelineMode.RIPPLE) {
      const affectedElements = this.getElementsAfterTime(
        track,
        element.end_time,
        element,
      );
      this.applyTimeDeltaToElements(
        affectedElements,
        -(newStartTime - originalTime),
      );
    }

    this.editor.timeline.recalculateDuration();

    // Mark as dirty
    this.markDirty(`trimElementStart (${element.id})`);

    // Notify observers
    this.notifyObservers(EditorChangeType.ELEMENT_TRIMMED_START, {
      element: element,
      previousTime: originalTime,
      previousDuration: originalDuration,
      newTime: snappedStartTime,
      newDuration: newDuration,
      previousTrimStart: originalTrimStart,
      newTrimStart: newTrimStart,
      mode: mode,
    });

    return true;
  }

  /**
   * Trims the media start of a video or audio element, keeping the timeline start time fixed,
   * adjusting duration, and optionally rippling subsequent elements.
   * @param element The video or audio element to trim
   * @param newTrimStart New trim_start value (seconds into the media)
   * @param mode Optional TimelineMode (RIPPLE shifts subsequent elements)
   * @returns true if successful, false otherwise
   */
  public trimMediaStart(
    element: Element,
    newTrimStart: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
    snapToFrame: boolean = true,
  ): boolean {
    // Only video or audio elements support trim_start, compositions can have video children
    if (
      !element ||
      (element.type !== 'video' &&
        element.type !== 'audio' &&
        element.type !== 'composition')
    )
      return false;

    // Ensure newTrimStart is non-negative and frame-snapped
    if (snapToFrame) {
      newTrimStart = this.editor.timeline.snapTimeToFrame(newTrimStart);
    }
    newTrimStart = Math.max(0, newTrimStart);

    // Get track
    const track = this.getTrackById(
      this.editor.timeline.getTrackIdForElement(element.id) || '',
    );
    if (!track) return false;

    // Extract original properties
    const originalTrimStart =
      pickUnderlyingVideoElement(element)?.trim_start ?? 0;
    const originalDuration = element.durationSeconds;

    // Calculate trim difference and new duration
    const trimDiff = newTrimStart - originalTrimStart;
    const newDuration = originalDuration - trimDiff;

    // Validate new duration
    if (newDuration <= 0) return false;

    // Split updates, they might be destined for different objects
    element.update({
      duration: newDuration,
    });
    pickUnderlyingVideoElement(element)?.update({
      trim_start: newTrimStart,
    });

    // Refresh transcript segment for video elements
    if (element.type === 'video') {
      (element as VideoElement).invalidateTranscriptSegment();
    }

    // In RIPPLE mode, shift subsequent elements
    if (mode === TimelineMode.RIPPLE) {
      const affectedElements = this.getElementsAfterTime(
        track,
        element.end_time,
        element,
      );
      this.applyTimeDeltaToElements(affectedElements, -trimDiff);
    }

    this.editor.timeline.recalculateDuration();
    this.markDirty(`trimMediaStart (${element.id})`);

    // Notify observers
    this.notifyObservers(EditorChangeType.ELEMENT_TRIMMED_MEDIA_START, {
      element: element,
      previousTrimStart: originalTrimStart,
      newTrimStart: newTrimStart,
      previousDuration: originalDuration,
      newDuration: newDuration,
      mode: mode,
    });

    return true;
  }

  /**
   * Adjusts the end of an element (both extending and trimming)
   * Handles both general and video-specific duration adjustment logic
   * @param element The element to adjust
   * @param newEndTime New end time for the element
   * @param mode Optional TimelineMode to use for this operation
   * @returns true if successful, false otherwise
   */
  public trimElementEnd(
    element: Element,
    newEndTime: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
    snapToFrame: boolean = true,
  ): boolean {
    if (!element) return false;

    // Apply frame snapping
    if (snapToFrame) {
      newEndTime = this.editor.timeline.snapTimeToFrame(newEndTime);
    }
    newEndTime = Math.max(0, newEndTime);

    const track = this.getTrackById(
      this.editor.timeline.getTrackIdForElement(element.id) || '',
    );
    if (!track) return false;

    if (newEndTime < element.time) return false; // Prevent invalid end time

    // Capture original state for video-specific calculations
    const currentEndTime = element.end_time;

    // Calculate the new duration
    let newDuration = newEndTime - element.time;

    // For DEFAULT mode with extension, handle snapping
    if (
      mode == TimelineMode.DEFAULT &&
      element.duration !== null &&
      newDuration > element.durationSeconds
    ) {
      // Treat as extend
      newDuration = this.calculateValidDuration(
        track,
        element,
        newEndTime,
        element.time,
        element.durationSeconds,
      );
    }

    if (newDuration <= 0) return false; // Prevent invalid duration

    // Final end time after any adjustments
    const finalEndTime = element.time + newDuration;

    // Apply all updates at once
    element.update({ duration: newDuration });

    // Ensure transcript segment is refreshed if it's a video element
    if (element.type === 'video') {
      const videoElement = element as VideoElement;
      videoElement.invalidateTranscriptSegment();
    }

    // Apply ripple effect if in ripple mode
    if (mode === TimelineMode.RIPPLE) {
      const timeDelta = finalEndTime - currentEndTime; // Positive if extending, negative if trimming

      if (timeDelta != 0) {
        const affectedElements = this.getElementsAfterTime(
          track,
          currentEndTime,
          element,
        );
        this.applyTimeDeltaToElements(affectedElements, timeDelta);
      }
    }

    // Recalculate timeline duration
    this.editor.timeline.recalculateDuration();

    return true;
  }

  /**
   * Calculates a start time that avoids collisions with other elements on the track
   * @param track The track to check for collisions
   * @param element The element being adjusted (to exclude from collision check)
   * @param requestedStartTime Requested start time
   * @param originalStartTime Original start time of the element
   * @param duration Duration of the element
   * @returns A valid start time that avoids collisions
   */
  private calculateValidStartTime(
    track: Track,
    element: Element,
    requestedStartTime: number,
    originalStartTime: number,
    duration: number,
  ): number {
    // Check moving forward...
    if (requestedStartTime > originalStartTime) {
      // Check following collisons
      const followingElements = this.getFollowingElements(element);
      const closestFollowingElement = followingElements[0];
      for (const nextElement of followingElements) {
        const nextElementStart = nextElement.time;
        // Check for collision between [requestedStartTime, requestedStartTime + duration] and [nextElement.time. nextElement.end_time]
        if (
          requestedStartTime < nextElement.end_time &&
          requestedStartTime + duration > nextElementStart
        ) {
          // Collision detected, move as close to requested time as possible
          // Example: originalTime 6, duration 3, requestedTime = 10, next element starts at 12, duration 3
          //          10 + 3 = 13, 13 > 12, so move to 12 - 3 = 9
          return closestFollowingElement.time - duration;
        }
      }
      // And backward
    } else {
      // Check previous collisions
      const previousElements = this.getPrecedingElements(element);
      const closestPrevElement = previousElements[previousElements.length - 1];

      for (const prevElement of previousElements) {
        // Check for collision between [requestedStartTime, requestedStartTime + duration] and [prevElement.time, prevElement.end_time]
        if (
          requestedStartTime < prevElement.end_time &&
          requestedStartTime + duration > prevElement.time
        ) {
          // Collision detected, move as close to requested time as possible
          // Example: originalTime 6, duration 3, requestedTime = 2, previous element starts at 0, duration 3
          //          2 + 3 = 5, 5 > 0, so move to 0 + 3 = 3
          return closestPrevElement.end_time;
        }
      }
    }

    return requestedStartTime;
  }

  /**
   * Calculates a duration that avoids collisions with other elements on the track
   * @param track The track to check for collisions
   * @param element The element being adjusted (to exclude from collision check)
   * @param requestedEndTime Requested end time
   * @param originalStartTime Original start time of the element
   * @param duration Duration of the element
   * @returns A valid duration that avoids collisions
   */
  private calculateValidDuration(
    track: Track,
    element: Element,
    requestedEndTime: number,
    originalStartTime: number,
    duration: number,
  ): number {
    // Calculate the requested duration using the element's start time
    const requestedDuration = requestedEndTime - originalStartTime;

    // Prevent negative duration
    if (requestedDuration <= 0) {
      return 0;
    }

    const originalEndTime = originalStartTime + duration;

    //  Handle extension beyond the original end time
    if (requestedEndTime > originalEndTime) {
      // Assume track has a method to get elements following this one
      const followingElements = this.getFollowingElements(element);
      const nextElement = followingElements[0];

      if (nextElement) {
        const nextElementStart = nextElement.time;
        if (requestedEndTime > nextElementStart) {
          // Collision detected: limit duration to the start of the next element
          return nextElementStart - originalStartTime;
        }
      }
    }

    // Return the requested duration if no collisions
    return requestedDuration;
  }
  /**
   * Creates a new track of the specified type and adds it to the timeline
   * @param type The type of track to create
   * @returns The newly created Track object
   */
  public createTrack(
    type: 'video' | 'audio' | 'text' | 'image' = 'video',
  ): Track {
    const track = this.editor.timeline.createTrack(type);

    // Mark as dirty
    this.markDirty(`createTrack (${track.id})`);

    // Notify observers
    this.notifyObservers(EditorChangeType.TRACK_ADDED, {
      track: track,
      trackId: track.id,
      type: type,
    });

    return track;
  }

  /**
   * Removes a track from the timeline
   * @param track The track to remove
   * @returns true if removed, false if the track doesn't exist
   */
  public removeTrack(track: Track): boolean {
    // Store track data for notification if needed
    const trackCopy = { ...track };
    const trackElements = [...track.elements];

    // Remove the track from the timeline
    const success = this.editor.timeline.removeTrack(track.id);

    if (success) {
      // Mark as dirty
      this.markDirty(`removeTrack (${track.id})`);

      // Notify observers
      this.notifyObservers(EditorChangeType.TRACK_REMOVED, {
        trackId: track.id,
        track: trackCopy,
        elements: trackElements,
      });
    }

    return success;
  }

  /**
   * Moves an element to a different track
   * @param element The element to move
   * @param targetTrack The target track
   * @returns true if successful, false otherwise
   */
  public moveElementToTrack(element: Element, targetTrack: Track): boolean {
    // Get source track ID
    const sourceTrackId = this.editor.timeline.getTrackIdForElement(element.id);
    if (!sourceTrackId) return false;

    // If source and target are the same, nothing to do
    if (sourceTrackId === targetTrack.id) return true;

    // Store original track for notifications
    const sourceTrack = this.editor.timeline.getTrack(sourceTrackId);
    if (!sourceTrack) return false;

    // Execute the move
    const success = this.editor.timeline.moveElementToTrack(
      element.id,
      targetTrack.id,
    );

    if (success) {
      // Mark as dirty
      this.markDirty(`moveElementToTrack (${element.id})`);

      // Notify observers
      this.notifyObservers(EditorChangeType.ELEMENT_TRACK_CHANGED, {
        element: element,
        previousTrack: sourceTrack,
        newTrack: targetTrack,
      });
    }

    return success;
  }

  /**
   * Removes an element from the timeline and optionally ripples subsequent elements.
   * @param element The ID of the element to remove.
   * @param mode The timeline mode (DEFAULT or RIPPLE). In RIPPLE mode, shifts subsequent elements to cover the gap.
   * @returns true if the element was successfully removed, false otherwise.
   */
  public removeElement(
    element: Element,
    mode: TimelineMode = TimelineMode.DEFAULT,
  ): boolean {
    // Capture the element's start time and duration before removal
    const startTime = element.time;
    const duration = element.durationSeconds;

    // Get the track containing the element
    const track = this.editor.timeline.getTrackForElement(element.id);
    if (!track) return false;

    // Remove the element from the track
    track.removeElement(element.id);

    // Handle ripple mode if duration is positive
    if (mode === TimelineMode.RIPPLE && duration > 0) {
      // Get all elements that start after the removed element's start time
      const elementsAfter = this.getElementsAfterTime(
        track,
        startTime,
        element,
      );
      // Shift them earlier by the duration of the removed element
      this.applyTimeDeltaToElements(elementsAfter, -duration);
    }

    // Update the timeline duration
    this.editor.timeline.recalculateDuration();

    // Mark the timeline as dirty
    this.markDirty(`removeElement (${element.id})`);

    // Notify any observers of the change
    this.notifyObservers(EditorChangeType.ELEMENT_REMOVED, {
      elementId: element.id,
      mode: mode,
    });

    return true;
  }

  /**
   * Adds an element to a track at its specified time, optionally rippling subsequent elements.
   * @param element The element to add
   * @param track The target track
   * @param mode TimelineMode to determine rippling behavior
   * @returns true if successful, false otherwise
   */
  public addElement(
    element: Element,
    track: Track,
    mode: TimelineMode = TimelineMode.DEFAULT,
  ): boolean {
    if (mode === TimelineMode.RIPPLE) {
      const elementsAfter = this.getElementsAfterTime(
        track,
        element.time,
        element,
      );
      this.applyTimeDeltaToElements(elementsAfter, element.durationSeconds);
    }
    this.editor.timeline.addElementToTrack(track.id, element);
    this.editor.timeline.recalculateDuration();
    this.markDirty(`addElementWithRipple (${element.id})`);
    this.notifyObservers(EditorChangeType.ELEMENT_ADDED, {
      element,
      trackId: track.id,
      mode,
    });
    return true;
  }

  /**
   * Merges multiple elements into a single element on the specified track.
   * Elements must be contiguous (no gaps between them).
   *
   * @param elements Array of elements to merge
   * @param targetTrack The track where the merged element will reside
   * @returns The merged Element or null if the operation fails
   */
  public mergeElements(
    elements: Element[],
    targetTrack: Track,
  ): Element | null {
    if (!this.validateMerge(elements)) {
      console.warn('Cannot merge elements: validation failed');
      return null;
    }

    const mergedProps = this.calculateMergedProperties(elements);

    // Create a new element based on the first element but with merged properties
    const mergedElement = elements[0].clone();
    mergedElement.update(mergedProps);

    // Ensure the new element has a unique ID
    mergedElement.id = `merged-${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 7)}`;

    // Remove all original elements
    elements.forEach((element) =>
      this.editor.timeline.removeElement(element.id),
    );

    // Add the merged element
    this.editor.timeline.addElementToTrack(targetTrack.id, mergedElement);

    // Ripple subsequent elements if needed
    const rippleAmount =
      mergedElement.durationSeconds -
      elements.reduce((acc, el) => acc + el.durationSeconds, 0);
    if (rippleAmount > 0) {
      const elementsAfter = this.getElementsAfterTime(
        targetTrack,
        mergedElement.time,
        mergedElement,
      );

      this.applyTimeDeltaToElements(elementsAfter, rippleAmount);
    }

    return mergedElement;
  }

  /**
   * Merges multiple elements into a single element, filling any gaps between them.
   * Unlike mergeElements, this allows non-contiguous elements to be merged by
   * bridging the gaps in the source media.
   *
   * Example: If element one has trim_start: 0, duration 5 and element two has
   * trim_start: 6, duration 4, there's a 1 second gap in media time (0..5_6..10).
   * This method will create a single element with trim_start: 0, duration 10.
   *
   * @param elements Array of elements to merge
   * @param targetTrack The track where the merged element will reside
   * @returns The merged Element or null if the operation fails
   */
  public bridgeAndMergeElements(
    elements: Element[],
    targetTrack: Track,
  ): Element | null {
    // Validate everything except contiguity
    if (!this.validateMerge(elements, true)) {
      console.warn('Cannot bridge and merge elements: validation failed');
      return null;
    }

    // Calculate properties with gap filling enabled
    const mergedProps = this.calculateMergedProperties(elements, true);

    // Create a new element based on the first element but with merged properties
    const mergedElement = elements[0].clone();
    mergedElement.update(mergedProps);

    // Ensure the new element has a unique ID
    mergedElement.id = Element.newKey('bridged');

    // Sort elements by time
    const sortedElements = [...elements].sort((a, b) => a.time - b.time);

    // Calculate the timeline span of all original elements combined
    const originalSpan = sortedElements.reduce(
      (acc, el) => acc + el.durationSeconds,
      0,
    );

    // Calculate the span from start of first to end of last (including gaps)
    const firstElement = sortedElements[0];
    const lastElement = sortedElements[sortedElements.length - 1];
    const totalSpan =
      lastElement.time + lastElement.durationSeconds - firstElement.time;

    // Remove all original elements
    elements.forEach((element) =>
      this.editor.timeline.removeElement(element.id),
    );

    // Add the merged element
    this.editor.timeline.addElementToTrack(targetTrack.id, mergedElement);

    // Calculate if subsequent elements need to be rippled
    // This happens when the new merged element covers a different total timeline span
    // than the sum of the individual element durations.  When gaps are bridged the
    // merged element becomes longer, so we need to pull all following elements *earlier*
    // by the bridged gap size so the overall timeline length remains unchanged.

    const rippleAmount = mergedElement.durationSeconds - originalSpan;

    // If the merged element duration increased (gap bridged), shift subsequent elements
    // earlier by the same amount so that the end of the merged range lines up with where
    // the last original element ended.
    if (rippleAmount > 0) {
      const elementsAfter = this.getElementsAfterTime(
        targetTrack,
        lastElement.time + lastElement.durationSeconds, // End of last original element
        mergedElement,
      );

      // Shift earlier – negative delta
      this.applyTimeDeltaToElements(elementsAfter, -rippleAmount);
    }

    // Refresh transcript if it's a video element
    if (mergedElement.type === 'video') {
      (mergedElement as VideoElement).invalidateTranscriptSegment();
    }

    return mergedElement;
  }

  /**
   * Splits an element into two at the specified time
   * @param element The element to split
   * @param splitTime The time (in seconds) where the split occurs
   * @returns A tuple of two new Elements or null if the operation fails
   */
  public splitElement(
    element: Element,
    splitTime: number,
  ): [Element, Element] | null {
    if (!element) return null;

    const trackId = this.editor.timeline.getTrackIdForElement(element.id) || '';

    if (!this.validateSplit(element, splitTime)) {
      console.warn('Cannot split element: invalid split time');
      return null;
    }

    const [firstElement, secondElement] = this.createSplitElements(
      element,
      splitTime,
    );

    // Remove the original element
    this.editor.timeline.removeElement(element.id);

    // Get the track and add the two new elements
    const track = this.editor.timeline.getTrack(trackId);
    if (!track) return null;

    this.editor.timeline.addElementToTrack(track.id, firstElement);
    this.editor.timeline.addElementToTrack(track.id, secondElement);

    return [firstElement, secondElement];
  }

  //
  // Utility methods
  //

  /**
   * Gets a track by its ID
   * @param trackId The ID of the track to find
   * @returns The Track object or undefined if not found
   * @deprecated Use direct Track references when possible
   */
  public getTrackById(trackId: string): Track | undefined {
    return this.editor.timeline.tracksMap.get(trackId);
  }

  /**
   * Gets an element by its ID
   * @param elementId The ID of the element to find
   * @returns The Element object or undefined if not found
   * @deprecated Use direct Element references when possible
   */
  public getElementById(elementId: string): Element | undefined {
    return this.editor.timeline.allElements.find(
      (element) => element.id === elementId,
    );
  }

  /**
   * Finds elements that overlap with a specified time range
   * @param startTime Start of the time range
   * @param endTime End of the time range
   * @param track Optional track to filter elements
   * @param elementType Optional element type to filter (e.g., 'video', 'audio')
   * @returns Array of elements that overlap with the time range
   */
  public findElementsInTimeRange(
    startTime: number,
    endTime: number,
    track?: Track,
    elementType?: string,
  ): Element[] {
    // Get elements from all tracks or a specific track
    let elements: Element[] = [];
    if (track) {
      elements = Array.from(track.elements);
    } else {
      // Use the timeline's allElements property (which is readonly)
      elements = Array.from(this.editor.timeline.allElements);
    }

    // Filter by element type if specified
    if (elementType) {
      elements = elements.filter((el) => el.type === elementType);
    }

    // Find elements that overlap with the time range
    // An element overlaps if it starts before the range ends and ends after the range starts
    return elements.filter((element) => {
      const elementStart = element.time;
      const elementDuration = element.durationSeconds;
      const elementEnd = elementStart + elementDuration;

      const epsilon = 0.001; // Small tolerance for floating-point comparison
      return (
        elementStart < endTime - epsilon && elementEnd > startTime + epsilon
      );
    });
  }

  /**
   * Updates an element's properties
   * @param element The element to update
   * @param properties Properties to update
   * @returns true if successful, false otherwise
   */
  public adjustElement(
    element: Element,
    properties: Partial<Element>,
  ): boolean {
    element.update(properties);

    return true;
  }

  /**
   * Creates a new element based on a template element
   * @param template The element to use as a template
   * @param properties New properties to apply
   * @returns The newly created element
   */
  public createElementFromTemplate(
    template: Element,
    properties: Partial<Element>,
  ): Element {
    // Clone the template element - this preserves all original properties
    const newElement = template.clone();

    // Apply the new properties
    for (const [key, value] of Object.entries(properties)) {
      // Use type assertions to safely apply properties
      (newElement as any)[key] = value;
    }

    // Ensure a unique ID
    newElement.id =
      properties.id ||
      `element-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;

    return newElement;
  }

  //
  // Utility Functions for Ripple Logic
  //

  /**
   * Retrieves elements on a track that start after a given time, excluding a specific element.
   * @param track The track to search
   * @param time The time threshold
   * @param excludeElement The element to exclude
   * @returns Sorted array of elements starting after the given time
   */
  private getElementsAfterTime(
    track: Track,
    time: number,
    excludeElement: Element,
  ): Element[] {
    return Array.from(track.elements)
      .filter((el) => el.id !== excludeElement.id && el.time >= time)
      .sort((a, b) => a.time - b.time);
  }

  /**
   * Gets all elements following a specified element on the same track
   * @param element The element to find following elements for
   * @param track Optional track to search in (will look up track if not provided)
   * @returns Array of elements that follow the specified element, sorted by time
   */
  public getFollowingElements(element: Element, track?: Track): Element[] {
    let targetTrack = track;

    if (!targetTrack) {
      const trackId = this.editor.timeline.getTrackIdForElement(element.id);
      if (!trackId) return [];

      targetTrack = this.getTrackById(trackId);
      if (!targetTrack) return [];
    }

    return Array.from(targetTrack.elements)
      .filter((el) => el.id !== element.id && el.time >= element.end_time)
      .sort((a, b) => a.time - b.time);
  }

  /**
   * Gets all elements preceding a specified element on the same track
   * @param element The element to find preceding elements for
   * @param track Optional track to search in (will look up track if not provided)
   * @returns Array of elements that precede the specified element, sorted by time
   */
  public getPrecedingElements(element: Element, track?: Track): Element[] {
    let targetTrack = track;

    if (!targetTrack) {
      const trackId = this.editor.timeline.getTrackIdForElement(element.id);
      if (!trackId) return [];

      targetTrack = this.getTrackById(trackId);
      if (!targetTrack) return [];
    }

    return Array.from(targetTrack.elements)
      .filter((el) => el.id !== element.id && el.end_time <= element.time)
      .sort((a, b) => a.time - b.time);
  }

  /**
   * Applies a time delta to a list of elements, shifting their start times.
   * @param elements The elements to shift
   * @param timeDelta The amount to shift each element's start time
   */
  private applyTimeDeltaToElements(
    elements: Element[],
    timeDelta: number,
  ): void {
    for (const el of elements) {
      const newTime = el.time + timeDelta;
      el.update({ time: newTime });
    }

    this.extendTimeDeltaToLayoutElements(elements, timeDelta);
  }

  private extendTimeDeltaToLayoutElements(
    elementsAfter: Element[],
    duration: number,
  ): void {
    const extraElementData = this._extraElementData;

    if (!extraElementData) {
      return;
    }

    const affectedElements = elementsAfter.filter((element) =>
      extraElementData.isLayoutParent(element),
    );

    for (const layoutElement of affectedElements) {
      const layoutResult =
        extraElementData.findLayoutRelatedElements(layoutElement);
      const children = layoutResult.layoutChildrenIds
        .map((id) => this.editor.timeline.getElement(id))
        .filter((el): el is Element => el !== null);

      for (const child of children) {
        const newTime = child.time + duration;
        child.update({ time: newTime });
      }
    }
  }

  //
  // Internal implementation helpers
  //

  /**
   * Validates that elements can be merged (same track, contiguous, same source)
   * @param elements Array of elements to validate
   * @returns true if the elements can be merged, false otherwise
   */
  private validateMerge(
    elements: Element[],
    skipContinuityCheck: boolean = false,
  ): boolean {
    if (elements.length < 2) {
      console.warn('Need at least two elements to merge');
      return false;
    }

    // Check if all elements are on the same track
    const trackIds = new Set(
      elements.map((el) => this.editor.timeline.getTrackIdForElement(el.id)),
    );
    if (trackIds.size !== 1) {
      console.warn('Elements must be on the same track to merge');
      return false;
    }

    // For audio/video elements, check if they all have the same source
    if (elements[0].type === 'audio' || elements[0].type === 'video') {
      // Get the source of the first element to compare with others
      const firstSource = (elements[0] as any).source;

      // Check if all elements have the same source
      const differentSources = elements.some((el) => {
        return (el as any).source !== firstSource;
      });

      if (differentSources) {
        console.warn('Cannot merge elements with different sources');
        return false;
      }
    }

    // Skip continuity check if specified (for bridgeAndMergeElements)
    if (skipContinuityCheck) {
      return true;
    }

    // Sort elements by time
    const sortedElements = [...elements].sort((a, b) => a.time - b.time);

    // Check if elements are contiguous (no gaps)
    for (let i = 0; i < sortedElements.length - 1; i++) {
      const current = sortedElements[i];
      const next = sortedElements[i + 1];

      const currentEnd = current.time + current.durationSeconds;
      const epsilon = 0.001; // Small tolerance for floating point comparison

      if (Math.abs(currentEnd - next.time) > epsilon) {
        console.warn(
          `Elements must be contiguous: gap between ${current.id} and ${next.id}`,
        );
        return false;
      }
    }

    return true;
  }

  /**
   * Calculates properties for the merged element
   * @param elements Array of elements to merge
   * @returns Object with properties for the merged element
   */
  private calculateMergedProperties(
    elements: Element[],
    fillGaps: boolean = false,
  ): Partial<Element> {
    if (elements.length === 0) return {};

    // Sort elements by time
    const sortedElements = [...elements].sort((a, b) => a.time - b.time);

    // Get first and last element as audio/video types
    const firstElement = sortedElements[0] as VideoElement | AudioElement;
    const lastElement = sortedElements[sortedElements.length - 1] as
      | VideoElement
      | AudioElement;
    const firstTrimStart = firstElement.trim_start ?? 0;
    const lastTrimStart = lastElement.trim_start ?? 0;

    // Calculate merged duration
    let mergedDuration: number;

    if (fillGaps) {
      // For bridgeAndMergeElements we want the merged element to span the *media* range
      // that all individual clips cover, not just the occupied timeline span.  Using
      // media bounds ensures that any gaps between the source clips (i.e. discontinuities
      // in the underlying video/audio) are included in the new element's duration so
      // that playback is seamless.

      // Example:
      //   clip A: trim_start 0,  duration 5  (media 0 → 5)
      //   clip B: trim_start 6,  duration 4  (media 6 → 10)   ← 1 s gap in media (5 → 6)
      //   timeline positions: A @0-5, B @5-9      (no timeline gap)
      //   merged element should cover media 0 → 10  ⇒ duration 10 (not 9)

      // Therefore we calculate duration using the media space:
      //   (last clip's media end) − (first clip's media start)

      const lastMediaEnd = lastTrimStart + lastElement.durationSeconds;
      mergedDuration = lastMediaEnd - firstTrimStart;
    } else {
      // Standard merge for contiguous elements: use media bounds
      // (last element's media end) - (first element's media start)
      mergedDuration =
        lastTrimStart + lastElement.durationSeconds - firstTrimStart;
    }

    // Base merged properties
    const baseProps: Partial<Element> = {
      time: firstElement.time,
      duration: mergedDuration,
    };

    // For audio/video elements, preserve source and trim_start from the first element
    if (firstElement.type === 'audio' || firstElement.type === 'video') {
      (baseProps as any).source = firstElement.source;
      (baseProps as any).trim_start = firstTrimStart;
    }

    return baseProps;
  }

  /**
   * Validates that the split time is valid for the given element
   * @param element The element to validate against
   * @param splitTime The time where the split would occur
   * @returns true if the split is valid, false otherwise
   */
  private validateSplit(element: Element, splitTime: number): boolean {
    const elementStartTime = element.time;
    const elementEndTime = element.time + element.durationSeconds;
    const epsilon = 0.001; // Small tolerance for floating point comparison

    // Split time must be strictly between start and end times (with small tolerance)
    if (
      splitTime <= elementStartTime + epsilon ||
      splitTime >= elementEndTime - epsilon
    ) {
      console.warn(
        `Split time ${splitTime} must be between element start ${elementStartTime} and end ${elementEndTime}`,
      );
      return false;
    }

    return true;
  }

  /**
   * Creates two new elements from a split operation
   * @param element The original element to split
   * @param splitTime The time where the split occurs
   * @returns Tuple of two new elements (before and after the split)
   */
  private createSplitElements(
    element: Element,
    splitTime: number,
  ): [Element, Element] {
    const elementStartTime = element.time;
    const elementDuration = element.durationSeconds;
    const elementEndTime = elementStartTime + elementDuration;

    // Calculate durations for the two new elements
    const firstDuration = splitTime - elementStartTime;
    const secondDuration = elementEndTime - splitTime;

    // Base properties for all element types
    const baseFirstElement = element.clone();
    baseFirstElement.id = Element.newKey('split-first');
    // Use proper setters via editor
    baseFirstElement.time = elementStartTime;
    baseFirstElement.duration = firstDuration;
    if (baseFirstElement.type === 'composition') {
      pickUnderlyingVideoElement(baseFirstElement)?.update({
        id: `inner-${baseFirstElement.id}`,
      });
    }

    const baseSecondElement = element.clone();
    baseSecondElement.id = Element.newKey('split-second');
    // Use proper setters via editor
    baseSecondElement.time = splitTime;
    baseSecondElement.duration = secondDuration;
    if (baseSecondElement.type === 'composition') {
      pickUnderlyingVideoElement(baseSecondElement)?.update({
        id: `inner-${baseSecondElement.id}`,
      });
    }

    // Handle properties that are specific to video/audio elements
    if (
      element.type === 'video' ||
      element.type === 'audio' ||
      element.type === 'composition'
    ) {
      const mediaElement = element as VideoElement | AudioElement;

      // Update trim_start for second element
      const trimSourceElement = pickUnderlyingVideoElement(mediaElement);
      const firstTrimEnd = (trimSourceElement?.trim_start ?? 0) + firstDuration;
      const trimDestinationElement =
        pickUnderlyingVideoElement(baseSecondElement);
      trimDestinationElement?.update({ trim_start: firstTrimEnd } as Element);

      (baseFirstElement as VideoElement | AudioElement).audio_fade_out = 0;
      (baseSecondElement as VideoElement | AudioElement).audio_fade_in = 0;

      // Ensure transcript segment is refreshed
      // TODO: Do we need to do thius?
      // videoElement.invalidateTranscriptSegment();
    }

    baseFirstElement.removeAnimation('', 'end');
    baseSecondElement.removeAnimation('', 'start');
    baseSecondElement.removeAnimation('', 0);

    return [baseFirstElement, baseSecondElement];
  }

  /**
   * Excises a specific media time range from a VideoElement, creating two new elements
   * that exclude the removed range while maintaining seamless timeline with a cut in
   * the source playback.
   *
   * @param element The VideoElement to excise from.
   * @param mediaRemovalStart The start time in media seconds to remove.
   * @param mediaRemovalEnd The end time in media seconds to remove.
   * @param mode Optional TimelineMode to use for this operation.
   * @returns An object containing the two new elements and the removed duration, or null if unsuccessful.
   */
  public exciseTimeRangeFromElement(
    element: Element,
    mediaRemovalStart: number,
    mediaRemovalEnd: number,
    mode: TimelineMode = TimelineMode.DEFAULT,
  ): {
    firstPart: Element;
    secondPart: Element;
    removedDuration: number;
  } | null {
    if (!element) return null;

    if (element.type !== 'video' && element.type !== 'composition') {
      console.warn(`Element ${element.id} is not a VideoElement.`);
      return null;
    }

    const trackId = this.editor.timeline.getTrackIdForElement(element.id);
    if (!trackId) return null;

    const track = this.getTrackById(trackId);
    if (!track) return null;

    // Trim Adjusted media start and end times
    const elementMediaStart =
      pickUnderlyingVideoElement(element)?.trim_start ?? 0;
    // const elementMediaEnd = elementMediaStart + element.durationSeconds;

    // EXAMPLE
    // Original duration: 10
    // mediaRemovalStart: 2
    // mediaRemovalEnd: 5

    // Description: Create two new elements:
    // 1. First part: { time: 0, duration: 2, trim_start: 0 }
    // 2. Second part: { time: 2, duration: 5, trim_start: 7 }
    // 3. Removed duration: 3
    // 4. New duration: 10 - 3 = 7
    // 5. New second trim_start: mediaRemovalStart + firstPartDuration + removedDuration
    //                                   2 + 2 + 3 = 7

    // Calculate durations
    // Removed duration is the difference between media removal start and end
    const removedDuration = mediaRemovalEnd - mediaRemovalStart;

    // First element duration (before the cut)
    // Duration is simply the cut point
    // ex: Original element should go from 0 to cut point (2)
    const firstPartDuration = mediaRemovalStart;
    // Trim start is unchanged
    // ex: original trim start 2, keep it the same
    const firstPartTrimStart = elementMediaStart; // Trim start is unchanged.

    // Second element duration (after the cut)
    // Duration is the original duration minus the removed duration and the first part duration
    // ex: original duration 10 - cut point 2 - removed duration 2 = 6
    const secondPartDuration =
      element.durationSeconds - mediaRemovalStart - removedDuration;
    // Trim start is the cut point
    // ex: original trim start 2, cut at 5, removed duration 2
    // second part trim start = 2 + 5 + 2 = 9
    const secondPartTrimStart =
      elementMediaStart + firstPartDuration + removedDuration;

    // Validate new duration
    if (secondPartDuration <= 0) {
      console.warn(
        `Invalid second part duration: ${secondPartDuration}. Cannot excise time range.`,
      );
      return null;
    }

    // Create first part
    const firstPart = element.clone();
    firstPart.update({
      id: Element.newKey('excised-first'),
      duration: firstPartDuration,
      time: element.time,
    });
    if (firstPart.type === 'composition') {
      pickUnderlyingVideoElement(firstPart)?.update({
        id: `inner-${firstPart.id}`,
      });
    }
    pickUnderlyingVideoElement(firstPart)?.update({
      trim_start: firstPartTrimStart,
    });

    // Create second part
    const secondPart = element.clone();
    secondPart.update({
      id: Element.newKey('excised-second'),
      duration: secondPartDuration,
      time: element.time + firstPartDuration,
    });
    pickUnderlyingVideoElement(secondPart)?.update({
      trim_start: secondPartTrimStart,
    });
    if (secondPart.type === 'composition') {
      pickUnderlyingVideoElement(secondPart)?.update({
        id: `inner-${secondPart.id}`,
      });
    }

    this.editor.timeline.removeElement(element.id);
    this.editor.timeline.addElementToTrack(track.id, firstPart);
    this.editor.timeline.addElementToTrack(track.id, secondPart);

    // Add Transcript to new elements.
    // TODO: Refactor Transcript segment management outside of VideoElement

    if (mode == TimelineMode.RIPPLE) {
      // Move all subsequent elements by the removed duration
      const affectedElements = this.getElementsAfterTime(
        track,
        secondPart.end_time,
        element,
      );
      this.applyTimeDeltaToElements(affectedElements, -removedDuration);
    }

    return { firstPart, secondPart, removedDuration };
  }

  /**
   *  Undo the insertion of an element by shifting subsequent elements back
   *  to their original positions and removing the inserted element.
   * @param element
   * @param shiftAmount
   * @param track
   */
  public undoInsertElement(
    element: Element,
    shiftAmount: number,
    track: Track,
  ): void {
    const elementsAfter = this.getElementsAfterTime(
      track,
      element.time,
      element,
    );
    this.applyTimeDeltaToElements(elementsAfter, -shiftAmount);
    this.removeElement(element, TimelineMode.DEFAULT);
  }

  /**
   * Reverses a merge operation by removing the merged element and restoring the original elements.
   * @param mergedElement The merged element to remove
   * @param originalElements The array of original elements to restore
   * @param track The track containing the elements
   */
  public undoMergeElements(
    mergedElement: Element,
    originalElements: Element[],
    track: Track,
  ): void {
    // Remove the merged element from the timeline
    this.removeElement(mergedElement, TimelineMode.DEFAULT);

    // Restore the original elements to the track
    originalElements.forEach((element) => {
      this.editor.timeline.addElementToTrack(track.id, element);
    });

    // Ripple difference between merged and original elements
    const rippleAmount =
      originalElements.reduce((acc, el) => acc + el.durationSeconds, 0) -
      mergedElement.durationSeconds;

    const elementsAfter = this.getElementsAfterTime(
      track,
      mergedElement.end_time,
      originalElements[originalElements.length - 1],
    );
    this.applyTimeDeltaToElements(elementsAfter, rippleAmount);

    // Recalculate timeline duration
    this.editor.timeline.recalculateDuration();
  }

  /**
   * Reverses a trim operation, restoring the element’s original state and reversing any ripple effect.
   * @param element The element that was trimmed
   * @param originalState The element’s state before the trim (e.g., { trim_start, duration })
   * @param track The track containing the element
   * @param rippleMode The mode used during the trim (e.g., RIPPLE or DEFAULT)
   */
  public undoTrimElement(
    element: Element,
    originalState: Record<string, any>,
    track: Track,
    rippleMode: TimelineMode,
  ): void {
    // Calculate the duration difference caused by the trim
    const originalDuration = originalState.duration;
    const newDuration = element.durationSeconds;
    const durationDiff = newDuration - originalDuration;

    // Restore the element to its original state (trim_start, duration, etc.)
    element.restoreState(originalState);

    // If ripple mode was used, reverse the ripple effect on subsequent elements
    if (rippleMode === TimelineMode.RIPPLE) {
      // Get elements after this one on the track
      const elementsAfter = this.getElementsAfterTime(
        track,
        element.end_time,
        element,
      );
      // Shift them back by the inverse of the duration change
      this.applyTimeDeltaToElements(elementsAfter, -durationDiff);
    }
  }

  /*
   * Shifts a track up or down in the layer stack
   * @param trackId ID of the track to shift
   * @param direction Direction to shift the track ('up' or 'down')
   * @returns ID of the target track if success, or null if failed
   */
  public shiftTrackLayer(
    trackId: string,
    direction: 'up' | 'down',
  ): string | null {
    // Validate track ID is within allowed range (1-31)
    const trackIdNum = Number(trackId);
    if (isNaN(trackIdNum) || trackIdNum < 1 || trackIdNum > 31) return null;

    const tracks = this.editor.timeline.tracks;
    const trackIndex = tracks.findIndex((track) => track.id === trackId);

    if (trackIndex === -1) return null;

    // Determine target track based on position
    let targetIndex: number;
    if (direction === 'up') {
      // Can't move the top track up further
      if (trackIndex >= tracks.length - 1) return null;
      targetIndex = trackIndex + 1;
    } else {
      // Can't move the bottom track down further
      if (trackIndex <= 0) return null;
      targetIndex = trackIndex - 1;
    }

    const targetTrack = tracks[targetIndex];
    if (!targetTrack) return null;
    // Validate target track ID is within allowed range (1-31)
    const targetTrackIdNum = Number(targetTrack.id);
    if (
      isNaN(targetTrackIdNum) ||
      targetTrackIdNum < 1 ||
      targetTrackIdNum > 31
    )
      return null;

    const targetTrackId = targetTrack.id;
    const success = this.swapTrackPositions(trackId, targetTrackId);

    if (success) return targetTrackId;

    return null;
  }

  /**
   * Swaps the positions of two tracks in the layer stack
   * @param trackId1 ID of the first track
   * @param trackId2 ID of the second track
   * @returns true if successful, false otherwise
   */
  public swapTrackPositions(trackId1: string, trackId2: string): boolean {
    const timeline = this.editor.timeline;
    const track1 = timeline.getTrack(trackId1);
    const track2 = timeline.getTrack(trackId2);

    if (!track1 || !track2) return false;

    // Store original IDs
    const track1Id = track1.id;
    const track2Id = track2.id;

    // Update track1 ID and its elements
    track1.setId(track2Id);
    for (const element of track1.elements) {
      element.update({ track: Number(track2Id) });
    }

    // Update track2 ID and its elements
    track2.setId(track1Id);
    for (const element of track2.elements) {
      element.update({ track: Number(track1Id) });
    }

    // Update the tracks in the timeline
    timeline.removeTrack(track1Id);
    timeline.removeTrack(track2Id);
    timeline.addExistingTrack(track1);
    timeline.addExistingTrack(track2);
    timeline.sortTracksByNumericId();

    return true;
  }

  set extraElementDataRaw(value: Video['extraElementData']) {
    this._extraElementData = new ExtraElementData(value);
  }

  get extraElementData(): ExtraElementData {
    return this._extraElementData || new ExtraElementData({});
  }
}
